import React, { useRef, useEffect, useCallback } from 'react'

const VIDEO_FALLBACK_TIMEOUT_MS = 10000 // 10s max wait before skipping to prize popup
const AUTOPLAY_CHECK_DELAY_MS = 1500 // time to wait before checking if video actually started

/**
 * WinVideo - Plays the win animation video with error/timeout fallback.
 *
 * Handles cases where the video is broken, unreachable, or autoplay is blocked
 * (e.g. iOS Low Power Mode), ensuring the user always sees the prize popup.
 */
const WinVideo = ({ src, type, onComplete }) => {
  const videoRef = useRef(null)
  const hasFiredRef = useRef(false)
  const timeoutRef = useRef(null)
  const autoplayCheckRef = useRef(null)

  const handleComplete = useCallback(() => {
    if (hasFiredRef.current) return
    hasFiredRef.current = true
    clearTimeout(timeoutRef.current)
    clearTimeout(autoplayCheckRef.current)
    onComplete()
  }, [onComplete])

  useEffect(() => {
    if (src) {
      // Safety timeout: if video hasn't ended or errored within the limit, proceed anyway
      timeoutRef.current = setTimeout(() => {
        handleComplete()
      }, VIDEO_FALLBACK_TIMEOUT_MS)

      // Autoplay detection: if after a short delay the video hasn't started playing,
      // it means autoplay was blocked (e.g. iOS Low Power Mode). Skip immediately.
      autoplayCheckRef.current = setTimeout(() => {
        const video = videoRef.current
        if (video && (video.paused || video.readyState < 2)) {
          handleComplete()
        }
      }, AUTOPLAY_CHECK_DELAY_MS)

      return () => {
        clearTimeout(timeoutRef.current)
        clearTimeout(autoplayCheckRef.current)
      }
    }
    // No video source provided, skip directly to prize popup
    handleComplete()
  }, [src, handleComplete])

  // If no source, render nothing (onComplete already fired above)
  if (!src) return null

  return (
    <video
      ref={videoRef}
      autoPlay
      muted
      playsInline
      preload="auto"
      onEnded={handleComplete}
      onError={handleComplete}
      style={{
        position: 'fixed',
        inset: 0,
        width: '100vw',
        height: '100vh',
        objectFit: 'cover',
        zIndex: '9000'
      }}>
      <source src={src} type={type} onError={handleComplete} />
    </video>
  )
}

export default WinVideo
